import { DatePicker } from "@rio-cloud/rio-uikit";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useT } from "@hooks";
import ToolbarActions, { type ToolbarActionsProps } from "./ToolbarActions";
import ToolbarCreateButton from "./ToolbarCreateButton";
import ToolbarSearch from "./ToolbarSearch";

const resolveMomentLocale = (locale: string) => {
  if (locale.startsWith("en")) {
    return "en-gb";
  }
  if (locale.startsWith("es")) {
    return "es";
  }
  return "pt-br";
};

export interface SharedToolbarProps extends ToolbarActionsProps {
  searchValue: string;
  startDate: string;
  endDate: string;
  createLabelKey: string;
  handleSearchValueChange: (newSearchValue: string) => void;
  handleStartDate: (date: string) => void;
  handleEndDate: (date: string) => void;
  handleCreate: () => void;
}

const SharedToolbar = ({
  searchValue,
  startDate,
  endDate,
  createLabelKey,
  handleSearchValueChange,
  handleStartDate,
  handleEndDate,
  handleCreate,
  ...actions
}: SharedToolbarProps) => {
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);
  const t = useT();

  const getStartDateValidation = (current: Moment) => {
    if (!endDate) return true;
    const minDate = moment(endDate).subtract(1, "year");
    const maxDate = moment(endDate);
    return current.isSameOrAfter(minDate, "day") && current.isSameOrBefore(maxDate, "day");
  };

  const getEndDateValidation = (current: Moment) => {
    if (!startDate) return true;
    const minDate = moment(startDate);
    const maxDate = moment(startDate).add(1, "year");
    return current.isSameOrAfter(minDate, "day") && current.isSameOrBefore(maxDate, "day");
  };

  const [inputStartDate, setInputStartDate] = useState<Moment | string>(
    moment(startDate, "YYYY-MM-DD").format("DD/MM/YYYY")
  );
  const [inputEndDate, setInputEndDate] = useState<Moment | string>(moment(endDate, "YYYY-MM-DD").format("DD/MM/YYYY"));

  useEffect(() => {
    setInputStartDate(moment(startDate, "YYYY-MM-DD").format("DD/MM/YYYY"));
  }, [startDate]);

  useEffect(() => {
    setInputEndDate(moment(endDate, "YYYY-MM-DD").format("DD/MM/YYYY"));
  }, [endDate]);

  const onChangeStartDate = (result: Moment | string, isValid: boolean) => {
    if (moment.isMoment(result)) {
      setInputStartDate(result);
    } else {
      if (result.length > 10) {
        return;
      }

      setInputStartDate(result);
    }

    if (isValid) {
      handleStartDate(moment(result).format("YYYY-MM-DD"));
    }
  };

  const onChangeEndDate = (result: Moment | string, isValid: boolean) => {
    if (moment.isMoment(result)) {
      setInputEndDate(result);
    } else {
      if (result.length > 10) {
        return;
      }

      setInputEndDate(result);
    }

    if (isValid) {
      handleEndDate(moment(result).format("YYYY-MM-DD"));
    }
  };

  return (
    <TableToolbar>
      <div className="table-toolbar-container">
        <div className="table-toolbar-group-left">
          <ToolbarCreateButton label={t(createLabelKey)} onClick={handleCreate} />
          <div className="table-toolbar-column display-flex flex-row flex-wrap gap-10 align-items-center">
            <div className="width-40pct-xs flex-1-1-xs width-150">
              <DatePicker
                id="1"
                dateFormat="DD/MM/YYYY"
                className="rounded-small margin-bottom-0"
                locale={momentLocale}
                timeFormat={false}
                value={inputStartDate}
                onChange={onChangeStartDate}
                isValidDate={getStartDateValidation}
              />
            </div>
            <div className="flex-0">
              <span className="text-normal text-size-14 text-color-gray">{t("common.label.dateTo")}</span>
            </div>
            <div className="width-40pct-xs flex-1-1-xs width-150">
              <DatePicker
                id="2"
                className="rounded-small margin-bottom-0"
                dateFormat="DD/MM/YYYY"
                locale={momentLocale}
                timeFormat={false}
                value={inputEndDate}
                onChange={onChangeEndDate}
                isValidDate={getEndDateValidation}
              />
            </div>
            <ToolbarSearch searchValue={searchValue} handleSearchValueChange={handleSearchValueChange} />
          </div>
        </div>

        <ToolbarActions {...actions} />
      </div>
    </TableToolbar>
  );
};

export default SharedToolbar;
